import { Box, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { getSyncStatus } from 'shared/utils/sync';

import PremiumStarIcon from '@/assets/svg/PremiumStarIcon';
import { SyncStatusBadge } from '@/components';
import { useDBSyncContext } from '@/context/DBSyncContext';
import { useSubscriptionContext } from '@/context/SubscriptionContext';
import { useNetwork } from '@/hooks';
import { useIsPremiumFeatureEnabled } from '@/hooks/useIsPremiumFeatureEnabled';
import { COLOR, ENTITY_COLOR } from '@/theme/colors';

import InitialsAvatar from '../InitialsAvatar';

import { Props } from './types';

function CurrentUserBox({ userAvatarUrl, userProfileData }: Props) {
    const { currentOrganisationSubscriptionPlan, isCurrentSubscriptionActive } =
        useSubscriptionContext();
    const isOnline = useNetwork();
    const { isPremiumFeatureEnabled } = useIsPremiumFeatureEnabled();

    const { isSyncInProgress, hasSyncIssue, isUnsynced } = useDBSyncContext();

    const syncStatus = useMemo(
        () =>
            getSyncStatus({
                hasSyncIssue,
                isOnline,
                isSyncInProgress,
                isUnsynced,
            }),
        [hasSyncIssue, isUnsynced, isOnline, isSyncInProgress],
    );

    const isPersonalSubscription =
        currentOrganisationSubscriptionPlan?.seats === 1;
    const isTeamSubscription =
        (currentOrganisationSubscriptionPlan?.seats || 0) > 1;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                flexDirection: 'row',
                height: '100%',
                width: '100%',
                pt: 2,
                px: '13px',
            }}
            data-test-id="OrganisationBox"
        >
            <Box sx={{ position: 'relative' }}>
                <InitialsAvatar
                    testID="UserBox-Avatar-Sidebar"
                    avatarUrl={userAvatarUrl}
                    size={'54px'}
                    sx={{
                        fontSize: 20,
                        fontWeight: 'normal',
                        mr: 2,
                    }}
                    firstName={userProfileData?.first_name ?? ''}
                    lastName={userProfileData?.last_name ?? ''}
                />
                {isCurrentSubscriptionActive &&
                    (isPersonalSubscription || isTeamSubscription) &&
                    isPremiumFeatureEnabled && (
                        <Box sx={{ position: 'absolute', top: 2, right: 12 }}>
                            <PremiumStarIcon
                                width={18}
                                height={18}
                                color={
                                    isPersonalSubscription
                                        ? COLOR.deepCerulean
                                        : ENTITY_COLOR.teams
                                }
                                secondaryColor={COLOR.white}
                            />
                        </Box>
                    )}
            </Box>

            <Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        maxWidth: 105,
                        gap: 1,
                    }}
                >
                    <Typography
                        fontSize={13}
                        fontWeight={700}
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            lineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                        }}
                    >
                        {userProfileData?.first_name}{' '}
                        {userProfileData?.last_name}
                    </Typography>
                </Box>
                <SyncStatusBadge status={syncStatus} />
            </Box>
        </Box>
    );
}

export default CurrentUserBox;
